// Copyright (c) 2026 Vigeos SASU. Tous droits réservés.
import React, { useEffect, useState } from 'react';
import {
  Alert, AlertTitle, Box, Button, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Grid, TextField, Typography,
} from '@mui/material';
import api from '../../utils/api';
import {
  toCents, channelNotConnectedDetail, euros, missingFromRejection, billingErrorMessage,
} from '../../utils/formatFacturation';
import { CHANNEL_LABEL } from './commun';

const ACTION_TITLES = {
  emettre: 'Émettre la facture',
  depot: 'Enregistrer le dépôt',
  mad: 'Mise à disposition du destinataire',
  rejet: 'Enregistrer un rejet',
  paiement: 'Enregistrer un encaissement',
  relance: 'Tracer une relance',
  annuler: 'Annuler la facture',
  supprimer: 'Supprimer le brouillon',
};

/**
 * Gestes sur une facture ouverte : émission, dépôt, mise à disposition, rejet,
 * encaissement, relance, annulation, suppression d'un brouillon. Un refus de
 * l'API s'affiche avec ses mentions manquantes, ou, pour un canal non branché,
 * avec les étapes qui restent à faire.
 */
export default function DialogueActionFacture({ action, canaux: channels, emetteurPret: issuerReady, onFermer: onClose, onFait: onDone }) {
  const type = action?.type;
  const f = action?.facture;
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [missing, setMissing] = useState(null);
  const [channelBlock, setChannelBlock] = useState(null);
  const [result, setResult] = useState(null);
  const [form, setForm] = useState({});

  useEffect(() => {
    setError(''); setMissing(null); setChannelBlock(null); setResult(null); setSending(false);
    if (!f) { setForm({}); return; }
    if (type === 'depot') setForm({ external_ref: '' });
    else if (type === 'mad') setForm({ at: '', external_ref: '' });
    else if (type === 'rejet') setForm({ reason: '' });
    else if (type === 'paiement') setForm({ montant: ((f.outstanding_cents || 0) / 100).toFixed(2), moyen: '', reference: '', at: '' });
    else if (type === 'relance') setForm({ canal: 'email', note: '' });
    else setForm({});
  }, [type, f]);

  if (!action || !f) return null;

  const invoiceChannel = channels.find((c) => c.code === f.channel);
  const operationalChannel = Boolean(invoiceChannel?.operationnel);

  const lancer = async () => {
    setSending(true); setError(''); setMissing(null); setChannelBlock(null);
    try {
      let r;
      if (type === 'emettre') {
        r = await api.post(`/invoices/${f.id}/emettre`);
      } else if (type === 'depot') {
        // Toujours le canal manuel, seul à déposer réellement : le canal porté
        // par la facture (portail public) rendrait 501.
        r = await api.post(`/invoices/${f.id}/deposer`, { canal: 'manuel', external_ref: form.external_ref });
      } else if (type === 'mad') {
        const body = {};
        // ISO 8601 avec fuseau : une date naïve ferait courir l'échéance à côté.
        if (form.at) body.at = new Date(form.at).toISOString();
        if (form.external_ref) body.external_ref = form.external_ref;
        r = await api.post(`/invoices/${f.id}/mise-a-disposition`, body);
      } else if (type === 'rejet') {
        r = await api.post(`/invoices/${f.id}/rejet`, { reason: form.reason });
      } else if (type === 'paiement') {
        const body = { amount_cents: toCents(form.montant) };
        if (form.moyen) body.moyen = form.moyen;
        if (form.reference) body.reference = form.reference;
        if (form.at) body.at = new Date(form.at).toISOString();
        r = await api.post(`/invoices/${f.id}/paiement`, body);
      } else if (type === 'relance') {
        const body = { canal: form.canal || 'email' };
        if (form.note) body.note = form.note;
        r = await api.post(`/invoices/${f.id}/relance`, body);
        setResult(r.data);
        onDone(r.data.facture, { garderOuvert: true });
        return;
      } else if (type === 'annuler') {
        r = await api.post(`/invoices/${f.id}/annuler`);
      } else if (type === 'supprimer') {
        await api.delete(`/invoices/${f.id}`);
        onDone(null);
        return;
      }
      onDone(r.data);
    } catch (e) {
      setError(billingErrorMessage(e, 'L\'opération a échoué'));
      setMissing(missingFromRejection(e));
      setChannelBlock(channelNotConnectedDetail(e));
    } finally {
      setSending(false);
    }
  };

  const valid = (() => {
    if (type === 'depot') return Boolean(form.external_ref && form.external_ref.trim());
    if (type === 'rejet') return (form.reason || '').trim().length >= 3;
    if (type === 'paiement') {
      const c = toCents(form.montant);
      return c !== null && c > 0;
    }
    if (type === 'emettre') return issuerReady;
    return true;
  })();

  return (
    <Dialog open onClose={sending ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{ACTION_TITLES[type]}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
          {f.number || 'brouillon non numéroté'} : {euros(f.total_ttc_cents)} TTC,
          dont {euros(f.total_ht_cents)} hors taxes.
        </Typography>

        {error && (
          <Alert severity={channelBlock ? 'warning' : 'error'} sx={{ mb: 2 }}>
            <AlertTitle>{channelBlock ? 'Ce canal n\'est pas branché' : 'Refus de l\'API'}</AlertTitle>
            {error}
            {missing && missing.length > 0 && (
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 3 }}>
                {missing.map((m) => <li key={m}><Typography variant="body2">{m}</Typography></li>)}
              </Box>
            )}
            {channelBlock && (
              <Box sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight="medium">Reste à faire côté exploitation :</Typography>
                <Box component="ol" sx={{ mt: 0.5, mb: 1, pl: 3 }}>
                  {(channelBlock.etapes || []).map((e) => <li key={e}><Typography variant="body2">{e}</Typography></li>)}
                </Box>
                <Typography variant="body2">Repli : {channelBlock.repli}</Typography>
              </Box>
            )}
          </Alert>
        )}

        {type === 'emettre' && (
          <Box>
            {!issuerReady ? (
              <Alert severity="error">
                Le SIRET de l&apos;émetteur est absent du serveur : l&apos;émission
                sera refusée en 422. Renseigner VIGEOS_SIRET avant de recommencer.
              </Alert>
            ) : (
              <Typography variant="body2">
                Un numéro définitif va être attribué à cette facture, les références
                du client vont être figées et l&apos;échéance posée. Un numéro
                attribué ne se libère pas : la facture ne pourra plus être supprimée,
                seulement annulée.
              </Typography>
            )}
          </Box>
        )}

        {type === 'depot' && (
          <Box>
            {!operationalChannel && (
              <Alert severity="info" sx={{ mb: 2 }}>
                <AlertTitle>
                  Canal de cette facture : {CHANNEL_LABEL[f.channel] || f.channel}, non branché
                </AlertTitle>
                Ce canal valide la facture mais ne sait pas encore la déposer : il
                rend un refus définitif, pas une erreur passagère. Le dépôt se fait
                donc par saisie en ligne sur le portail, et la référence rendue par
                le portail est enregistrée ici. C&apos;est le parcours normal des
                premières factures.
                {(invoiceChannel?.etapes_restantes || []).length > 0 && (
                  <Box component="ol" sx={{ mt: 1, mb: 0, pl: 3 }}>
                    {invoiceChannel.etapes_restantes.map((e) => (
                      <li key={e}><Typography variant="body2">{e}</Typography></li>
                    ))}
                  </Box>
                )}
              </Alert>
            )}
            <TextField fullWidth size="small" label="Référence rendue au dépôt"
              value={form.external_ref || ''}
              onChange={(e) => setForm((x) => ({ ...x, external_ref: e.target.value }))}
              helperText="Exigée : sans elle le dépôt n'est pas prouvable, et l'API le refuse" />
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              Le dépôt sera enregistré sur le canal manuel. La mise à disposition au
              destinataire, qui fait courir le délai de paiement, reste à saisir
              ensuite : le dépôt manuel ne l&apos;horodate pas.
            </Typography>
          </Box>
        )}

        {type === 'mad' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              C&apos;est le point de départ opposable du délai de paiement, et il
              recalcule l&apos;échéance. La date d&apos;émission n&apos;entre pas
              dans ce calcul.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="datetime-local" label="Mise à disposition"
                  InputLabelProps={{ shrink: true }} value={form.at || ''}
                  onChange={(e) => setForm((x) => ({ ...x, at: e.target.value }))}
                  helperText="Vide : maintenant" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Référence (facultatif)" value={form.external_ref || ''}
                  onChange={(e) => setForm((x) => ({ ...x, external_ref: e.target.value }))} />
              </Grid>
            </Grid>
            {!f.payment_terms_days && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Cette facture ne porte pas de délai de paiement : l&apos;API refusera
                l&apos;enregistrement, car l&apos;échéance calculée serait fausse. Le
                délai se pose sur le site, avant l&apos;émission.
              </Alert>
            )}
          </Box>
        )}

        {type === 'rejet' && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>Le délai de paiement repart à zéro</AlertTitle>
              La date de mise à disposition et l&apos;échéance vont être effacées. La
              facture sort de la liste des impayés et n&apos;y reviendra qu&apos;après
              un nouveau dépôt suivi d&apos;une nouvelle mise à disposition. Ce
              n&apos;est pas une simple note.
            </Alert>
            <TextField fullWidth size="small" multiline rows={3} label="Motif du rejet, tel que rendu par le destinataire"
              value={form.reason || ''} onChange={(e) => setForm((x) => ({ ...x, reason: e.target.value }))}
              helperText="3 caractères au minimum" />
          </Box>
        )}

        {type === 'paiement' && (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Aucune correction n&apos;est possible après coup : l&apos;API ne sait ni
              annuler ni corriger un encaissement, et deux envois enregistrent deux
              encaissements. Vérifier le montant avant de valider.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Montant encaissé (€ TTC)" value={form.montant || ''}
                  onChange={(e) => setForm((x) => ({ ...x, montant: e.target.value }))}
                  helperText={`Reste dû : ${euros(f.outstanding_cents)}`} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" type="datetime-local" label="Date de l'encaissement"
                  InputLabelProps={{ shrink: true }} value={form.at || ''}
                  onChange={(e) => setForm((x) => ({ ...x, at: e.target.value }))}
                  helperText="Vide : maintenant" />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Moyen (virement, prélèvement...)" value={form.moyen || ''}
                  onChange={(e) => setForm((x) => ({ ...x, moyen: e.target.value }))} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth size="small" label="Référence bancaire" value={form.reference || ''}
                  onChange={(e) => setForm((x) => ({ ...x, reference: e.target.value }))} />
              </Grid>
            </Grid>
          </Box>
        )}

        {type === 'relance' && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Cette action <strong>n&apos;envoie rien</strong>. Elle compte et trace
              la relance, ce qui permet de dire au téléphone « deuxième relance le 12,
              sans réponse ». Le courrier reste à envoyer à la main.
            </Alert>
            <Grid container spacing={2}>
              <Grid item xs={5}>
                <TextField fullWidth size="small" label="Canal employé" value={form.canal || ''}
                  onChange={(e) => setForm((x) => ({ ...x, canal: e.target.value }))} />
              </Grid>
              <Grid item xs={7}>
                <TextField fullWidth size="small" label="Note" value={form.note || ''}
                  onChange={(e) => setForm((x) => ({ ...x, note: e.target.value }))} />
              </Grid>
            </Grid>
            {result && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Relance de niveau {result.niveau_relance}. {result.envoi}
              </Alert>
            )}
          </Box>
        )}

        {type === 'annuler' && (
          <Typography variant="body2">
            La facture passera au statut annulé. Aucun avoir n&apos;est produit, cette
            notion n&apos;existe pas dans le dispositif livré, et une facture annulée
            ne revient pas.
          </Typography>
        )}

        {type === 'supprimer' && (
          <Typography variant="body2">
            Suppression définitive de ce brouillon, sans corbeille. C&apos;est aussi
            la seule façon de corriger un brouillon : il n&apos;existe aucune route de
            modification, on supprime et on recrée.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={sending}>Fermer</Button>
        {!(type === 'relance' && result) && (
          <Button
            variant="contained"
            color={type === 'supprimer' || type === 'annuler' || type === 'rejet' ? 'error' : 'primary'}
            disabled={!valid || sending}
            onClick={lancer}
          >
            {sending ? <CircularProgress size={18} /> : 'Confirmer'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
